import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Sizes/variants follow concept2 prototype (docs/studio/design-concept2/src/primitives.jsx Alert).
// padding 14/16 px ≈ py-3.5 px-4; gap 12 = gap-3; rounded-[var(--radius)] = rounded-lg
// (theme-tokens.css maps --radius-lg = var(--radius)).
//
// Variants use opacity-mixed token backgrounds for low-emphasis status containers
// (success/destructive/warning/info each ~6-8% bg + ~30% border alpha).
const alertVariants = cva(
  "flex gap-3 rounded-lg px-4 py-3.5",
  {
    variants: {
      variant: {
        default: "border border-border bg-background",
        success:
          "border border-success/30 bg-success/[0.08]",
        destructive:
          "border border-destructive/30 bg-destructive/[0.06]",
        warning:
          "border border-warning/30 bg-warning/[0.08]",
        info:
          "border border-info/30 bg-info/[0.08]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof alertVariants> {
  /** Optional leading icon node (rendered with flex-shrink:0). */
  icon?: React.ReactNode
  /** Optional title — bold line above the description. */
  title?: React.ReactNode
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, title, children, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <div className="flex-1 text-sm">
        {title ? <div className="mb-0.5 font-semibold">{title}</div> : null}
        {children ? (
          <div className="text-[13px] text-muted-foreground">{children}</div>
        ) : null}
      </div>
    </div>
  )
)
Alert.displayName = "Alert"

export { Alert, alertVariants }
